/**
 * Tenant Features Page — Super Admin Only
 *
 * Manages tenant-level feature configuration and navigation visibility.
 * Template provides the baseline (presets); this page shows tenant overrides.
 *
 * Sections:
 * 1. Navigation Visibility — toggle groups/items on/off, drag to reorder
 * 2. (Future) Feature Flags — tenant-specific feature toggles
 * 3. (Future) AI Configuration overrides
 *
 * Only accessible by super_admin when in tenant context.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DraggableAttributes,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';
import { useAdminContext } from '../contexts/AdminContextProvider';
import { useAuthFetch } from '../hooks/useAuthFetch';
import type { ViewContext } from './StubPage';

// SyntheticListenerMap is not part of @dnd-kit/core's public type exports
// eslint-disable-next-line @typescript-eslint/ban-types
type SyntheticListenerMap = Record<string, Function>;

interface NavItem {
  id: string;
  label: string;
  icon?: string;
}

interface NavGroup {
  id: string;
  label: string;
  icon?: string;
  items: NavItem[];
}

interface TenantData {
  id: string;
  name: string;
  slug: string;
  template_id?: string;
  navigation_overrides: Record<string, unknown>;
}

// ── Drag handle icon (braille pattern dots) ──────────────────────────────────
const GripHandle: React.FC<{ size?: 'group' | 'item'; listeners?: SyntheticListenerMap; attributes?: DraggableAttributes }> = ({
  size = 'group',
  listeners,
  attributes,
}) => (
  <span
    {...listeners}
    {...attributes}
    style={{
      cursor: 'grab',
      opacity: 0.4,
      fontSize: size === 'group' ? '16px' : '13px',
      lineHeight: 1,
      flexShrink: 0,
      userSelect: 'none',
      touchAction: 'none',
      display: 'inline-flex',
      alignItems: 'center',
    }}
    title="Drag to reorder"
  >
    ⠿
  </span>
);

// ── Sortable group wrapper ────────────────────────────────────────────────────
interface SortableGroupProps {
  group: NavGroup;
  isGroupHidden: boolean;
  isExpanded: boolean;
  hiddenItems: Set<string>;
  itemOrder: string[];
  onToggleGroup: (id: string) => void;
  onToggleItem: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onItemDragEnd: (event: DragEndEvent, groupId: string) => void;
}

const SortableGroup: React.FC<SortableGroupProps> = ({
  group,
  isGroupHidden,
  isExpanded,
  hiddenItems,
  itemOrder,
  onToggleGroup,
  onToggleItem,
  onToggleExpand,
  onItemDragEnd,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: group.id,
    disabled: isGroupHidden,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform) || 'translateZ(0)',
    transition: transition || 'opacity 0.2s ease',
    opacity: isDragging ? 0.4 : isGroupHidden ? 0.5 : 1,
    border: '1px solid var(--glass-border, rgba(255,255,255,0.08))',
    borderRadius: '10px',
    overflow: 'hidden',
    backfaceVisibility: 'hidden' as never,
    zIndex: isDragging ? 1 : 'auto',
  };

  // Items in display order
  const orderedItems: NavItem[] = itemOrder
    .map(id => group.items.find(item => item.id === id))
    .filter((item): item is NavItem => item !== undefined);

  const visibleCount = orderedItems.filter(item => !hiddenItems.has(item.id)).length;

  const itemSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  return (
    <div ref={setNodeRef} style={style}>
      {/* Group header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 14px',
          background: 'var(--bg-tertiary, rgba(255,255,255,0.03))',
          cursor: 'pointer',
        }}
        onClick={() => !isGroupHidden && onToggleExpand(group.id)}
      >
        {/* Drag handle — left edge */}
        {!isGroupHidden && (
          <GripHandle size="group" listeners={listeners} attributes={attributes} />
        )}
        {isGroupHidden && (
          <span style={{ width: '18px', flexShrink: 0 }} />
        )}

        {/* Expand arrow */}
        <span style={{ fontSize: '10px', color: 'var(--text-secondary)', width: '12px', textAlign: 'center', flexShrink: 0 }}>
          {isGroupHidden ? '' : isExpanded ? '\u25BC' : '\u25B6'}
        </span>

        <span style={{
          flex: 1,
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--text-primary)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {group.label}
        </span>

        {isGroupHidden ? (
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            (all {group.items.length} items hidden)
          </span>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            {visibleCount}/{group.items.length}
          </span>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); onToggleGroup(group.id); }}
          style={{
            width: '44px', height: '24px', borderRadius: '12px', border: 'none',
            background: isGroupHidden
              ? 'var(--bg-secondary, rgba(255,255,255,0.15))'
              : 'var(--success, #22c55e)',
            position: 'relative', cursor: 'pointer',
            transition: 'background 0.2s ease', flexShrink: 0,
          }}
        >
          <span style={{
            position: 'absolute', top: '2px',
            left: isGroupHidden ? '2px' : '22px',
            width: '20px', height: '20px', borderRadius: '50%',
            background: '#fff', transition: 'left 0.2s ease',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }} />
        </button>
      </div>

      {/* Items */}
      {isExpanded && !isGroupHidden && (
        <DndContext
          sensors={itemSensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => onItemDragEnd(e, group.id)}
        >
          <SortableContext items={orderedItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <div style={{ padding: '4px 0' }}>
              {orderedItems.map(item => (
                <SortableItem
                  key={item.id}
                  item={item}
                  isItemHidden={hiddenItems.has(item.id)}
                  onToggleItem={onToggleItem}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

// ── Sortable item row ─────────────────────────────────────────────────────────
interface SortableItemProps {
  item: NavItem;
  isItemHidden: boolean;
  onToggleItem: (id: string) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ item, isItemHidden, onToggleItem }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px 10px 36px',
    opacity: isDragging ? 0.4 : isItemHidden ? 0.5 : 1,
    transition: transition || 'opacity 0.15s ease',
    transform: CSS.Transform.toString(transform),
    borderTop: '1px solid rgba(255,255,255,0.03)',
    background: isDragging ? 'rgba(255,255,255,0.03)' : undefined,
    zIndex: isDragging ? 1 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <GripHandle size="item" listeners={listeners} attributes={attributes} />
      <span style={{
        flex: 1,
        fontSize: '13px',
        color: isItemHidden ? 'var(--text-secondary)' : 'var(--text-primary)',
      }}>
        {item.label}
      </span>
      {isItemHidden && (
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic', flexShrink: 0 }}>
          hidden
        </span>
      )}
      <button
        onClick={() => onToggleItem(item.id)}
        style={{
          width: '40px', height: '22px', borderRadius: '11px', border: 'none',
          background: isItemHidden
            ? 'var(--bg-secondary, rgba(255,255,255,0.15))'
            : 'var(--success, #22c55e)',
          position: 'relative', cursor: 'pointer',
          transition: 'background 0.2s ease', flexShrink: 0,
        }}
      >
        <span style={{
          position: 'absolute', top: '2px',
          left: isItemHidden ? '2px' : '20px',
          width: '18px', height: '18px', borderRadius: '50%',
          background: '#fff', transition: 'left 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }} />
      </button>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const FeaturesPage: React.FC<{ context?: ViewContext }> = () => {
  const { activeTenant, incrementSchemaVersion } = useAdminContext();
  const { authFetch } = useAuthFetch();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [templateGroups, setTemplateGroups] = useState<NavGroup[]>([]);
  const [templateSlug, setTemplateSlug] = useState('');

  // Toggle state
  const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(new Set());
  const [hiddenItems, setHiddenItems] = useState<Set<string>>(new Set());
  const [initialHiddenGroups, setInitialHiddenGroups] = useState<string[]>([]);
  const [initialHiddenItems, setInitialHiddenItems] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Order state
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [itemOrderMap, setItemOrderMap] = useState<Record<string, string[]>>({});
  const [initialGroupOrder, setInitialGroupOrder] = useState<string[]>([]);
  const [initialItemOrder, setInitialItemOrder] = useState<Record<string, string[]>>({});

  // Drag overlay
  const [activeId, setActiveId] = useState<string | null>(null);

  const groupSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // ── Change detection ────────────────────────────────────────────────────────
  const hasChanges =
    JSON.stringify([...hiddenGroups].sort()) !== JSON.stringify([...initialHiddenGroups].sort()) ||
    JSON.stringify([...hiddenItems].sort()) !== JSON.stringify([...initialHiddenItems].sort()) ||
    JSON.stringify(groupOrder) !== JSON.stringify(initialGroupOrder) ||
    JSON.stringify(itemOrderMap) !== JSON.stringify(initialItemOrder);

  // ── Load tenant + template data ─────────────────────────────────────────────
  useEffect(() => {
    if (!activeTenant?.id) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch tenant details
        const tenantRes = await authFetch(`/api/admin/tenants/${activeTenant.id}`);
        if (!tenantRes.ok) throw new Error('Failed to load tenant');
        const tenantData = await tenantRes.json();
        setTenant(tenantData);

        const overrides = tenantData.navigation_overrides || {};
        const hg = (overrides.hiddenGroups as string[]) || [];
        const hi = (overrides.hiddenItems as string[]) || [];
        setHiddenGroups(new Set(hg));
        setHiddenItems(new Set(hi));
        setInitialHiddenGroups(hg);
        setInitialHiddenItems(hi);

        // Fetch effective schema to get template info
        const schemaRes = await authFetch(`/api/tenant-portal/effective-schema`);
        if (schemaRes.ok) {
          const schema = await schemaRes.json();
          setTemplateSlug(schema.template_slug || '');

          // Fetch raw template navigation
          if (schema.template_slug) {
            const templatesRes = await authFetch('/api/admin/templates');
            if (templatesRes.ok) {
              const data = await templatesRes.json();
              const templates = data.templates || data;
              const tmpl = Array.isArray(templates)
                ? templates.find((t: { slug: string }) => t.slug === schema.template_slug)
                : null;
              if (tmpl?.navigation?.groups) {
                const groups: NavGroup[] = tmpl.navigation.groups;
                setTemplateGroups(groups);

                // Initialize group order (use saved order if present, else template order)
                const savedGroupOrder = overrides.groupOrder as string[] | undefined;
                const defaultGroupOrder = groups.map(g => g.id);
                // Merge: keep saved order for known IDs, append any new ones at end
                const mergedGroupOrder = savedGroupOrder
                  ? [
                      ...savedGroupOrder.filter(id => defaultGroupOrder.includes(id)),
                      ...defaultGroupOrder.filter(id => !savedGroupOrder.includes(id)),
                    ]
                  : defaultGroupOrder;
                setGroupOrder(mergedGroupOrder);
                setInitialGroupOrder(mergedGroupOrder);

                // Initialize item order per group
                const savedItemOrder = overrides.itemOrder as Record<string, string[]> | undefined;
                const defaultItemOrder: Record<string, string[]> = {};
                groups.forEach(g => {
                  const defaultOrder = g.items.map(i => i.id);
                  const savedOrder = savedItemOrder?.[g.id];
                  defaultItemOrder[g.id] = savedOrder
                    ? [
                        ...savedOrder.filter(id => defaultOrder.includes(id)),
                        ...defaultOrder.filter(id => !savedOrder.includes(id)),
                      ]
                    : defaultOrder;
                });
                setItemOrderMap(defaultItemOrder);
                setInitialItemOrder(defaultItemOrder);
              }
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [activeTenant?.id, authFetch]);

  // ── Toggle handlers ─────────────────────────────────────────────────────────
  const toggleGroup = (groupId: string) => {
    setHiddenGroups(prev => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  const toggleItem = (itemId: string) => {
    setHiddenItems(prev => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  const toggleExpand = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  // ── DnD handlers ────────────────────────────────────────────────────────────
  const handleGroupDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleGroupDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setGroupOrder(prev => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleItemDragEnd = useCallback((event: DragEndEvent, groupId: string) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItemOrderMap(prev => {
      const order = prev[groupId] ?? [];
      const oldIndex = order.indexOf(String(active.id));
      const newIndex = order.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return prev;
      return { ...prev, [groupId]: arrayMove(order, oldIndex, newIndex) };
    });
  }, []);

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!tenant) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const newOverrides = {
        ...(tenant.navigation_overrides || {}),
        hiddenGroups: [...hiddenGroups],
        hiddenItems: [...hiddenItems],
        groupOrder,
        itemOrder: itemOrderMap,
      };

      const res = await authFetch(`/api/admin/tenants/${tenant.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ navigation_overrides: newOverrides }),
      });

      if (!res.ok) throw new Error('Failed to save');
      setInitialHiddenGroups([...hiddenGroups]);
      setInitialHiddenItems([...hiddenItems]);
      setInitialGroupOrder([...groupOrder]);
      setInitialItemOrder({ ...itemOrderMap });
      incrementSchemaVersion();
      setSuccess('Navigation visibility saved.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setHiddenGroups(new Set());
    setHiddenItems(new Set());
    const defaultGroupOrder = templateGroups.map(g => g.id);
    setGroupOrder(defaultGroupOrder);
    const defaultItemOrder: Record<string, string[]> = {};
    templateGroups.forEach(g => { defaultItemOrder[g.id] = g.items.map(i => i.id); });
    setItemOrderMap(defaultItemOrder);
  };

  // ── Computed ────────────────────────────────────────────────────────────────
  const orderedGroups: NavGroup[] = groupOrder
    .map(id => templateGroups.find(g => g.id === id))
    .filter((g): g is NavGroup => g !== undefined);

  // Drag overlay label
  const activeGroup = activeId ? templateGroups.find(g => g.id === activeId) : null;

  // ── Guard renders ───────────────────────────────────────────────────────────
  if (!activeTenant) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>
          Enter a tenant context to manage features.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>
          Loading tenant features...
        </div>
      </div>
    );
  }

  const totalGroups = templateGroups.length;
  const totalItems = templateGroups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Features</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Configure which features and navigation items are available for this tenant.
            The template <strong>{templateSlug || 'none'}</strong> provides the baseline; overrides are applied per-tenant.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '800px' }}>

        {/* Section: Navigation Visibility */}
        <div className="glass-card" style={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Navigation Visibility</h2>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                Toggle functional areas and individual views on/off. Drag the ⠿ handle to reorder. Hidden items won't appear in the tenant's sidebar.
              </p>
            </div>
            {templateSlug && (
              <div style={{
                fontSize: '11px', padding: '4px 10px',
                background: 'var(--bg-tertiary, rgba(255,255,255,0.05))',
                borderRadius: '6px', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
              }}>
                Source: {templateSlug}
              </div>
            )}
          </div>

          {/* Stats */}
          <div style={{
            display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-secondary)',
            padding: '8px 12px', background: 'var(--bg-tertiary, rgba(255,255,255,0.03))',
            borderRadius: '8px', marginBottom: '12px',
          }}>
            <span>{totalGroups} groups</span>
            <span>{totalItems} items</span>
            {(hiddenGroups.size > 0 || hiddenItems.size > 0) && (
              <span style={{ color: 'var(--warning, #f59e0b)' }}>
                {hiddenGroups.size > 0 && `${hiddenGroups.size} group${hiddenGroups.size > 1 ? 's' : ''} hidden`}
                {hiddenGroups.size > 0 && hiddenItems.size > 0 && ', '}
                {hiddenItems.size > 0 && `${hiddenItems.size} item${hiddenItems.size > 1 ? 's' : ''} hidden`}
              </span>
            )}
          </div>

          {error && (
            <div style={{
              padding: '8px 12px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px',
              background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)',
            }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{
              padding: '8px 12px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px',
              background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.2)',
            }}>
              {success}
            </div>
          )}

          {templateGroups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)', fontSize: '13px' }}>
              No template assigned to this tenant. Assign a template to manage navigation visibility.
            </div>
          ) : (
            <>
              {/* Toggle tree with group-level DnD */}
              <DndContext
                sensors={groupSensors}
                collisionDetection={closestCenter}
                onDragStart={handleGroupDragStart}
                onDragEnd={handleGroupDragEnd}
              >
                <SortableContext items={groupOrder} strategy={verticalListSortingStrategy}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {orderedGroups.map(group => (
                      <SortableGroup
                        key={group.id}
                        group={group}
                        isGroupHidden={hiddenGroups.has(group.id)}
                        isExpanded={expandedGroups.has(group.id)}
                        hiddenItems={hiddenItems}
                        itemOrder={itemOrderMap[group.id] ?? group.items.map(i => i.id)}
                        onToggleGroup={toggleGroup}
                        onToggleItem={toggleItem}
                        onToggleExpand={toggleExpand}
                        onItemDragEnd={handleItemDragEnd}
                      />
                    ))}
                  </div>
                </SortableContext>

                {/* Drag overlay for groups */}
                <DragOverlay>
                  {activeGroup ? (
                    <div style={{
                      border: '1px solid var(--glass-border, rgba(255,255,255,0.15))',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      background: 'var(--bg-secondary, rgba(30,30,40,0.95))',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      transform: 'translateZ(0)',
                    }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '12px 14px',
                        background: 'var(--bg-tertiary, rgba(255,255,255,0.05))',
                      }}>
                        <GripHandle size="group" />
                        <span style={{ fontSize: '10px', color: 'var(--text-secondary)', width: '12px', textAlign: 'center' }}>
                          {expandedGroups.has(activeGroup.id) ? '\u25BC' : '\u25B6'}
                        </span>
                        <span style={{ flex: 1, fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {activeGroup.label}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleReset}
                  disabled={hiddenGroups.size === 0 && hiddenItems.size === 0}
                >
                  Reset All
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  style={{ minWidth: '120px' }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Section: Future — Feature Flags */}
        <div className="glass-card" style={{ opacity: 0.6 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600 }}>Feature Flags</h2>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
            Tenant-specific feature toggles (AI access, self-service portal, backup scheduling, etc.) will be configurable here in a future update.
          </p>
        </div>

        {/* Section: Future — AI Configuration */}
        <div className="glass-card" style={{ opacity: 0.6 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600 }}>AI Configuration</h2>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
            Per-tenant AI model access, token limits, and orchestration settings will be manageable here in a future update.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeaturesPage;
